// @flow
import React, {useState, useEffect, useRef} from "react";
import {useObserver} from "mobx-react-lite";
import GoogleMapReact from "google-map-react";
import {useStore} from "../../hooks/useStore";
import {useFetch} from "../../hooks/useFetch";
import {
    FilterRow,
    SelectField,
    MapContainer,
    DatePickerField,
    TimePickerField
} from "./heatMapStyles";
import {Col, Row, Radio} from "antd";
import apiKey from "../../_core/apiKey";
import {cities} from "../../constants/constants";
import {arrayAlphabeticalSort} from "../../utils/helpers";
import moment from "moment";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useTranslation} from "react-i18next";
import {CityFilter, CompanyFilter} from "../_defaultComponents/FilterComponents";

export const Heatmap = () => {
    const [center, setCenter] = useState({lat: 41.11, lng: 29.11});
    const [type, setType] = useState("order");
    const [selectedCity, setSelectedCity] = useState("istanbul");
    const [company, setCompany] = useState("all");
    const [subCompany, setSubCompany] = useState("all");
    const [upperGroup, setupperGroup] = useState("all");
    const [regionGroup, setRegionGroup] = useState("all");
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [startTime, setStartTime] = useState();
    const [endTime, setEndTime] = useState();
    const [interval, setInterval] = useState("range");
    const [dateValidText, setDateValidText] = useState("");

    const [heat, setHeat] = useState([]);
    const stores = useStore();
    const {t} = useTranslation(["heatMap"]);

    const checkValidity = () => {
        if (interval === "range") {
            return true;
        } else {
            if (startDate && endDate && startTime && endTime) {
                return true;
            } else {
                return false;
            }
        }
    };

    const [heatMapLoading, heatMapData, setheatMapData, setheatMapLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.heatMapStore.getHeatMap.bind(null, {
            type: type,
            city: selectedCity,
            companyId: company,
            subcompanyId: subCompany,
            startDate: startDate?.format("YYYY-MM-DD"),
            endDate: endDate?.format("YYYY-MM-DD"),
            startTime: startTime?.format("HH:mm"),
            endTime: endTime?.format("HH:mm"),
            interval: interval,
            regionGroupId: regionGroup,
            upperRegionGroup: upperGroup
        }),
        dependencies: [
            type,
            selectedCity,
            interval,
            regionGroup,
            upperGroup,
            company,
            subCompany,
            startTime,
            endTime,
            startDate,
            endDate
        ],
        condition: checkValidity()
    });

    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            upperGroup: upperGroup,
            regionGroups: regionGroup,
            limit: 1000
        }),
        dependencies: [company, regionGroup, upperGroup],
        initialCall: false
    });

    const [upperGroupsLoading, upperGroups, setUpperGroups, setUpperGroupsLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId: company,
            city: selectedCity
        }),
        dependencies: [selectedCity],
        initialCall: true
    });

    const [regionGroupsLoading, regionGroups, setRegionGroups, setRegionGroupsLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.regionStore.getRegionGroups.bind(null, {
            companyId: company,
            city: selectedCity,
            upperGroup: upperGroup
        }),
        dependencies: [company, selectedCity, upperGroup],
        condition: company && selectedCity,
        initialCall: false
    });

    useEffect(() => {
        const positions = heatMapData.length > 0 ? heatMapData : [];
        const options = {
            radius: 22,
            opacity: 0.6
        };
        setHeat({positions, options});
    }, [heatMapData]);

    useEffect(() => {
        if (interval === "range") {
            setDateValidText("");
        } else if (interval === "cycle" && startDate && endDate && startTime && endTime) {
            setDateValidText("");
        } else {
            if (!(startDate && endDate) && startTime && endTime) {
                setDateValidText(`(${t("heatMap.validation.date", {ns: "heatMap"})})`);
            } else if (startDate && endDate && !(startTime && endTime)) {
                setDateValidText(`(${t("heatMap.validation.time", {ns: "heatMap"})})`);
            } else {
                setDateValidText(`(${t("heatMap.validation.dateAndTime", {ns: "heatMap"})})`);
            }
        }
    }, [interval, startDate, endDate, startTime, endTime]);

    return useObserver(() => (
        <div data-testid="heatmapScreen">
            <FilterRow gutter={[20, 20]} data-testid="filterCard">
                <Col span={4}>
                    <h5>{t("heatMap.filters.mapType", {ns: "heatMap"})}</h5>
                    <SelectField
                        data-testid="mapType"
                        value={type}
                        onChange={value => {
                            setType(value);
                        }}
                    >
                        <SelectField.Option value="pickup" data-testid="mapType_option">
                            {t("heatMap.fields.mapTypes.productPickupPoint", {ns: "heatMap"})}
                        </SelectField.Option>
                        <SelectField.Option value="order" data-testid="mapType_option">
                            {t("heatMap.fields.mapTypes.orderDeliveryPoint", {ns: "heatMap"})}
                        </SelectField.Option>
                    </SelectField>
                </Col>
                <Col span={4}>
                    <CityFilter
                        value={selectedCity}
                        onChange={(value, option) => {
                            setSelectedCity(value);
                            setCenter(option.center);
                        }}
                    />
                </Col>
                <Col span={4}>
                    <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                    <SelectField
                        data-testid="upperGroupSelect"
                        value={upperGroup}
                        onChange={value => {
                            setupperGroup(value);
                            setRegionGroups([]);
                        }}
                    >
                        <SelectField.Option value="all" data-testid="upperGroupSelect_option">
                            {t("filters.all", {ns: "common"})}
                        </SelectField.Option>
                        {upperGroups.map(upperGroup => (
                            <SelectField.Option
                                key={upperGroup.group}
                                value={upperGroup.group}
                                data-testid="upperGroupSelect_option"
                            >
                                {upperGroup.group}
                            </SelectField.Option>
                        ))}
                    </SelectField>
                </Col>
                <Col span={4}>
                    <CompanyFilter
                        showAllOption={true}
                        showPool={false}
                        allKey="all"
                        value={company}
                        onChange={value => {
                            setCompany(value);
                            setRegionGroups([]);
                        }}
                    />
                </Col>
                <Col span={4}>
                    <h5>{t("fields.regionGroup", {ns: "common"})}</h5>
                    <SelectField
                        data-testid="regionGroupSelect"
                        value={regionGroup}
                        onChange={value => {
                            setRegionGroup(value);
                        }}
                    >
                        <SelectField.Option value="all" data-testid="regionGroupSelect_option">
                            {t("filters.all", {ns: "common"})}
                        </SelectField.Option>
                        {regionGroups.map(regionGroup => (
                            <SelectField.Option
                                key={regionGroup._id}
                                value={regionGroup._id}
                                data-testid="regionGroupSelect_option"
                            >
                                {regionGroup.name}
                            </SelectField.Option>
                        ))}
                    </SelectField>
                </Col>
                <Col span={4}>
                    <h5>{t("fields.subcompany", {ns: "common"})}</h5>
                    <SelectField
                        data-testid="subCompanySelect"
                        value={subCompany}
                        onChange={value => {
                            setSubCompany(value);
                        }}
                        disabled={!company}
                    >
                        <SelectField.Option value="all" data-testid="subCompanySelect_option">
                            {t("filters.all", {ns: "common"})}
                        </SelectField.Option>
                        {subCompanies.rows &&
                            subCompanies?.rows.map(subCompany => (
                                <SelectField.Option
                                    key={subCompany._id}
                                    value={subCompany._id}
                                    data-testid="subCompanySelect_option"
                                >
                                    {subCompany.name}
                                </SelectField.Option>
                            ))}
                    </SelectField>
                </Col>
                <Col span={6}>
                    <h5>{t("heatMap.filters.startDate", {ns: "heatMap"})}</h5>
                    <DatePickerField
                        data-testid="startDatePicker"
                        value={startDate}
                        format="DD/MM/YYYY"
                        placeholder={t("other.selectDate", {ns: "common"})}
                        onSelect={(date, dateString) => {
                            setStartDate(date);
                        }}
                        onChange={(date, dateString) => {
                            setStartDate(date);
                        }}
                        locale={locale}
                    />
                    <TimePickerField
                        data-testid="startTimePicker"
                        value={startTime}
                        format="HH:00"
                        placeholder={t("other.selectTime", {ns: "common"})}
                        onSelect={(time, timeString) => {
                            setStartTime(time);
                        }}
                        onChange={(time, timeString) => {
                            setStartTime(time);
                        }}
                        locale={locale}
                    />
                </Col>
                <Col span={6}>
                    <h5>{t("heatMap.filters.endDate", {ns: "heatMap"})}</h5>
                    <DatePickerField
                        data-testid="endDatePicker"
                        value={endDate}
                        format="DD/MM/YYYY"
                        placeholder={t("other.selectDate", {ns: "common"})}
                        onSelect={(date, dateString) => {
                            setEndDate(date);
                        }}
                        onChange={(date, dateString) => {
                            setEndDate(date);
                        }}
                        locale={locale}
                    />
                    <TimePickerField
                        data-testid="endTimePicker"
                        value={endTime}
                        format="HH:00"
                        placeholder={t("other.selectTime", {ns: "common"})}
                        onSelect={(time, timeString) => {
                            setEndTime(time);
                        }}
                        onChange={(time, timeString) => {
                            setEndTime(time);
                        }}
                        locale={locale}
                    />
                </Col>
                <Col span={2}>
                    <h5>{t("heatMap.filters.dataType", {ns: "heatMap"})}</h5>
                    <Radio.Group
                        data-testid="dataTypeRadioGroup"
                        style={{display: "flex"}}
                        onChange={e => {
                            setInterval(e.target.value);
                        }}
                        value={interval}
                    >
                        <Radio value="range" data-testid="dataTypeRadio">
                            {t("heatMap.fields.dataTypes.range", {ns: "heatMap"})}
                        </Radio>
                        <Radio value="cycle" data-testid="dataTypeRadio">
                            {t("heatMap.fields.dataTypes.cycle", {ns: "heatMap"})}{" "}
                            <span style={{color: "red"}}>{dateValidText}</span>
                        </Radio>
                    </Radio.Group>
                </Col>
            </FilterRow>
            <Row type="flex" justify="center">
                <Col md={24} sm={12}>
                    <MapContainer data-testid="map">
                        <GoogleMapReact
                            center={center}
                            bootstrapURLKeys={{
                                key: apiKey,
                                language: "tr",
                                libraries: ["visualization"]
                            }}
                            yesIWantToUseGoogleMapApiInternals
                            zoom={stores.meStore.map.settings.zoom}
                            options={{
                                fullscreenControl: false,
                                zoomControl: false,
                                gestureHandling: "greedy"
                            }}
                            onChange={e => {
                                stores.meStore.map.settings.zoom = e.zoom;
                                stores.meStore.map.settings.center = e.center;
                            }}
                            heatmapLibrary={true}
                            heatmap={heat}
                        ></GoogleMapReact>
                    </MapContainer>
                </Col>
            </Row>
        </div>
    ));
};
